import { App, Form, Modal } from "antd"
import { useState } from "react"

import { TextInput } from "../../../components/Input/TextInput.tsx"
import { RichTextEditor } from "../../../components/RichTextEditor/RichTextEditor.tsx"
import { isRichTextEmpty } from "../../../components/RichTextEditor/rich-text-html.ts"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { uploadEmailTemplateImage } from "../api/services.ts"
import type { EmailTemplateMutateData } from "../types/email-template.types.ts"

export interface EmailTemplateFormModalProps {
  open: boolean
  mode: "create" | "edit" | "duplicate"
  initialValues?: Partial<EmailTemplateMutateData>
  existingNames: string[]
  onClose: () => void
  onSubmit: (values: EmailTemplateMutateData) => Promise<boolean>
}

const TITLES: Record<EmailTemplateFormModalProps["mode"], string> = {
  create: "New Email Template",
  edit: "Edit Email Template",
  duplicate: "Duplicate Email Template"
}

export function EmailTemplateFormModal({
  open,
  mode,
  initialValues,
  existingNames,
  onClose,
  onSubmit
}: EmailTemplateFormModalProps) {
  const { message } = App.useApp()
  const [form] = Form.useForm<EmailTemplateMutateData>()
  const [submitting, setSubmitting] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)

  const handleImageUpload = async (file: File): Promise<{ url: string }> => {
    setUploadingImage(true)
    try {
      const response = await uploadEmailTemplateImage(file)
      // A successful POST arrives enveloped (`{status, details: {..., data}}`)
      // — reading `response.data.url` directly always read `undefined`, which
      // is why a real, successful upload still showed this same error toast.
      const data = response.ok
        ? unwrapEnvelope<{ url: string }>(response.data)
        : undefined
      if (!data?.url) {
        void message.error("Couldn't upload the image. Please try again.")
        throw new Error("Image upload failed")
      }
      return { url: data.url }
    } finally {
      setUploadingImage(false)
    }
  }

  const handleOk = async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()
      // Stay open on a failed save so the typed values survive a retry —
      // onSubmit's own error toast already told the user why.
      if (await onSubmit({ ...values, name: values.name.trim() })) onClose()
    } catch {
      // validation error already shown inline
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={TITLES[mode]}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText={"Save"}
      cancelText={"Cancel"}
      destroyOnHidden
      maskClosable={false}
      // Closing while the save OR an image upload is still in flight (Cancel,
      // the × icon) would read to the user as "nothing happened", but a
      // request that later succeeds anyway creates the template — or, for an
      // upload, calls back into an editor this modal has already destroyed
      // (destroyOnHidden) — regardless.
      closable={!submitting && !uploadingImage}
      cancelButtonProps={{ disabled: submitting || uploadingImage }}
      okButtonProps={{ loading: submitting }}
      width={560}
    >
      <Form<EmailTemplateMutateData>
        form={form}
        layout={"vertical"}
        clearOnDestroy
        validateTrigger={["onSubmit", "onChange"]}
        initialValues={initialValues}
      >
        <Form.Item<EmailTemplateMutateData>
          label={"Template Name"}
          name={"name"}
          rules={[
            {
              validator: (_, value?: string) => {
                const trimmed = value?.trim()
                if (!trimmed) return Promise.reject(new Error("Give the template a name."))
                const clash = existingNames.some(
                  (name) => name.trim().toLowerCase() === trimmed.toLowerCase()
                )
                return clash
                  ? Promise.reject(
                      new Error(
                        "A template with this name already exists. Please choose a different name."
                      )
                    )
                  : Promise.resolve()
              }
            }
          ]}
        >
          <TextInput placeholder={"e.g. Customer Follow-Up"} maxLength={255} />
        </Form.Item>

        <Form.Item<EmailTemplateMutateData>
          label={"Subject"}
          name={"subject"}
          rules={[{ required: true, whitespace: true, message: "Give the email a subject." }]}
        >
          <TextInput placeholder={"e.g. Following Up on Your Project"} maxLength={255} />
        </Form.Item>

        <Form.Item<EmailTemplateMutateData>
          label={"Email Body"}
          name={"body"}
          rules={[
            {
              validator: (_, value?: string) =>
                isRichTextEmpty(value)
                  ? Promise.reject(new Error("Give the email a body."))
                  : Promise.resolve()
            }
          ]}
        >
          <RichTextEditor
            value={""}
            onChange={() => undefined}
            placeholder={"Hi [Customer Name],\n\n…"}
            minHeight={200}
            ariaLabel={"Email Body"}
            onImageUpload={handleImageUpload}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
